import { Component, Input, Output, EventEmitter, ChangeDetectorRef, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';

/** Kamera açısı tipleri */
export type KameraAcisi = 'drone' | 'yakin' | 'profil' | 'ust' | 'takim' | 'epik' | 'beat';

/** Director Mode durumu */
export interface DirectorDurum {
  aktif: boolean;
  kayitYapiliyor: boolean;
  muzikYuklendi: boolean;
  suankiAci: KameraAcisi;
}

@Component({
  selector: 'fiko-director-mode',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-director-mode.component.html',
  styleUrls: ['./fiko-director-mode.component.less']
})
export class FikoDirectorModeComponent implements OnDestroy {

  @ViewChild('muzikInput') muzikInputRef: ElementRef<HTMLInputElement>;

  /** FikoDialogCore pencere referansi */
  private _pencere: any;
  @Input() set pencere(v: any) {
    this._pencere = v?.instance || v;
  }
  get pencere(): any { return this._pencere; }

  /** Ana sahne referansı (anaGosterim içeren) */
  @Input() sahne: any;

  /** Simülasyon noktaları (takım yolu) */
  @Input() noktalar: any[] = [];

  /** Aktif simülasyon pozisyonu (poz index) */
  @Input() poz: number = 0;

  /** Aktif nokta bilgisi */
  @Input() aktifNokta: any;

  /** Simülasyon oynatılıyor mu */
  @Input() oynatiliyor: boolean = false;

  /** Simülasyon component referansı (canlı veri için) */
  @Input() simComponent: any;

  @Output() durumDegisti = new EventEmitter<DirectorDurum>();
  @Output() kameraDegisti = new EventEmitter<KameraAcisi>();
  @Output() kayitBitti = new EventEmitter<Blob>();

  // === Temel Durum ===
  aktif: boolean = false;
  suankiAci: KameraAcisi = 'drone';
  otomatikAciDegisimi: boolean = true;
  dramatikZoom: boolean = true;

  // === Kamera Ayarları ===
  kameraHizi: number = 50;
  zoomSiddeti: number = 60;
  aciDegisimSikligi: number = 50;

  // === Kayıt Sistemi ===
  kayitYapiliyor: boolean = false;
  kayitSuresi: number = 0;
  kayitBaslamaZamani: number = 0;
  private mediaRecorder: MediaRecorder;
  private kayitChunks: Blob[] = [];
  kayitKalitesi: number = 2500000;

  // === Müzik Sistemi ===
  muzikYuklendi: boolean = false;
  muzikOynatiliyor: boolean = false;
  muzikIsim: string = '';
  muzikSure: number = 0;
  muzikPozisyon: number = 0;
  muzikSes: number = 70;
  private audioCtx: AudioContext;
  private audioSource: AudioBufferSourceNode;
  private audioGain: GainNode;
  private audioBuffer: AudioBuffer;
  private muzikBaslangicZamani: number = 0;

  // === Beat Detection & Ritim Senkron ===
  ritimSenkron: boolean = true;           // Ritim senkronizasyonu açık/kapalı
  ritimSiddeti: number = 70;              // Ritim efekt şiddeti (0-100)
  private audioAnalyser: AnalyserNode;
  private analyserData: Uint8Array<ArrayBuffer>;
  private sonBeatZamani: number = 0;
  private beatEnerji: number = 0;         // Anlık beat enerjisi (0-1)
  private beatEnerjiSmooth: number = 0;   // Yumuşatılmış enerji
  private oncekiEnerji: number = 0;
  private enerjiGecmisi: number[] = [];   // Son N frame enerji (ortalama için)
  private beatZoomPulse: number = 0;      // Zoom pulse efekti (0-1)
  private beatShakeOffset = new THREE.Vector3(); // Kamera shake offset

  // === Beat Modu Ayarları (UI'dan kontrol edilir) ===
  beatTakimTakip: boolean = true;         // Takımı takip et (kapalıysa workpiece merkezi)
  beatOrbitHizi: number = 50;             // Orbit (döndürme) hızı (0-100)
  beatZoomEfekti: number = 60;            // Zoom pulse şiddeti (0-100)
  beatShakeEfekti: number = 50;           // Kamera sarsıntı şiddeti (0-100)
  beatAciAtlama: boolean = true;          // Beat'te açı atlama
  beatOrbitYonu: number = 1;              // Orbit yönü: 1=saat yönü, -1=ters
  beatHareketTipi: 'smooth' | 'aggressive' | 'pulse' = 'smooth'; // Hareket stili

  // Beat modu için özel değişkenler
  private beatModAci: number = 0;         // Beat modunda orbit açısı
  private beatModMesafe: number = 150;    // Beat modunda kamera mesafesi
  private beatModYukseklik: number = 100; // Beat modunda kamera yüksekliği
  private beatModHedefAci: number = 0;    // Beat anında hedef açı
  private beatModSonPozisyon: number = 0; // Beat modunda son pozisyon (0-3 kadran)
  private beatModIntensity: number = 0;   // Beat yoğunluğu (yumuşatılmış)
  private beatModMevcutTarget = new THREE.Vector3(); // Mevcut hedef (takım veya merkez)

  // === Beat Modu - Sabit Kamera Seçeneği ===
  beatSabitKamera: boolean = false;  // Sabit kamera - sadece zoom efekti (hareket yok)
  beatSabitTakimIzle: boolean = true; // Sabit modda takımı izle (target takımı takip eder)

  // === SERBEST KAMERA & DEMO BEAT ===
  serbestKamera: boolean = false;    // Serbest mod - kullanıcı manuel zoom/pan yapabilir
  demoBeat: boolean = false;         // Demo beat - müzik yokken simüle beat (test için)
  private demoBeatZamani: number = 0;
  private demoBeatInterval: number = 500; // Demo beat aralığı (ms)

  // === GELİŞMİŞ AYARLAR (TAM ÖZELLEŞTİRME) ===

  // Beat Algılama Ayarları
  beatHassasiyet: number = 65;       // Beat algılama hassasiyeti (0-100, yüksek = daha hassas)
  beatCooldownAyar: number = 150;    // Beat arası minimum süre (ms)
  beatBassAgirlik: number = 70;      // Bass frekans ağırlığı (0-100)
  beatMidAgirlik: number = 30;       // Mid frekans ağırlığı (0-100)

  // === BEAT TEPKİ ŞİDDETLERİ (Ritme göre ne kadar etki edecek) ===
  // Zoom/FOV Tepkisi
  beatZoomTepki: number = 60;        // Beat'te zoom in şiddeti (0-100)
  beatZoomGeriDonus: number = 40;    // Zoom'un geri dönüş hızı (0-100, düşük = yavaş)

  // Hareket Tepkisi
  beatOrbitTepki: number = 50;       // Beat'te orbit hız artışı (0-100)
  beatMesafeTepki: number = 40;      // Beat'te kamera yaklaşma şiddeti (0-100)
  beatYukseklikTepki: number = 50;   // Beat'te kamera yükselme şiddeti (0-100)

  // Efekt Tepkileri
  beatShakeTepki: number = 50;       // Beat'te kamera sarsıntı şiddeti (0-100)
  beatTargetTepki: number = 40;      // Beat'te bakış noktası salınım şiddeti (0-100)

  // Genel Tepki Ayarları
  beatTepkiHizi: number = 50;        // Beat tepkilerinin uygulanma hızı (0-100, yüksek = hızlı/keskin)
  beatEnerjiCarpani: number = 50;    // Beat enerjisinin çarpan etkisi (0-100, yüksek = güçlü beat'lere daha fazla tepki)

  // Gelişmiş ayarlar paneli açık/kapalı
  gelismisAyarlarAcik: boolean = false;

  // === Sinematik Kamera Sistemi ===
  private animFrameId: number;
  private kameraGecisProgress: number = 1;
  private sonAciDegisimZamani: number = 0;
  private oncekiHareketTuru: string = '';

  // Takım takibi için
  private takimPoz = new THREE.Vector3();
  private takimPozOnceki = new THREE.Vector3();
  private takimHiz = new THREE.Vector3();           // Anlık hız vektörü
  private takimYonu = new THREE.Vector3(0, 0, 1);   // Takım ekseni yönü (spindle yukarı = Z+)
  private hareketYonu = new THREE.Vector3(1, 0, 0); // Hareket yönü (normalize edilmiş)
  private hareketYonuSmooth = new THREE.Vector3(1, 0, 0); // Yumuşatılmış hareket yönü

  // Kamera geçiş vektörleri
  private kameraMevcutPoz = new THREE.Vector3();
  private kameraMevcutTarget = new THREE.Vector3();
  private kameraHedefPoz = new THREE.Vector3();
  private kameraHedefTarget = new THREE.Vector3();

  // Drone orbit state
  private droneAci: number = 0;
  private droneYukseklik: number = 0;
  private droneMesafe: number = 0;

  // Workpiece bounds (for framing)
  private workpieceMerkez = new THREE.Vector3();
  private workpieceBoyut = new THREE.Vector3(100, 100, 50);
  private workpieceMinZ: number = 0;  // Workpiece alt yüzeyi
  private workpieceMaxZ: number = 50; // Workpiece üst yüzeyi
  private minKameraZ: number = 60;    // Kamera minimum yüksekliği

  // Cinematic timing
  private modBaslangicZamani: number = 0;
  private kesimAnindaZoom: boolean = false;

  // === Preset Kamera Açıları ===
  readonly KAMERA_ACILARI: { [key in KameraAcisi]: { isim: string, ikon: string, aciklama: string } } = {
    'drone': { isim: 'Drone', ikon: 'video', aciklama: 'Orbital takip - takımın etrafında döner' },
    'yakin': { isim: 'Close-up', ikon: 'search plus', aciklama: 'Dramatik yakın çekim - kesim noktasına odaklanır' },
    'profil': { isim: 'Profil', ikon: 'arrows alternate horizontal', aciklama: 'Yandan takip - takımla birlikte hareket' },
    'ust': { isim: 'Top-Down', ikon: 'arrow down', aciklama: 'Kuş bakışı - işleme yolunu gösterir' },
    'takim': { isim: 'Tool POV', ikon: 'eye', aciklama: 'Takım perspektifi - takımın arkasından bakış' },
    'epik': { isim: 'Epic', ikon: 'film', aciklama: 'Sinematik geniş çekim - yavaş dolly hareketi' },
    'beat': { isim: 'Beat', ikon: 'music', aciklama: 'Müzik ritmine göre hareket - beat\'e senkronize kamera' }
  };

  constructor(private cd: ChangeDetectorRef) {}

  ngOnDestroy() {
    this.durdur();
    this.muzikDurdur();
    this.kayitDurdur();
  }

  // ════════════════════════════════════════════════════════════════════════
  // ANA KONTROLLER
  // ════════════════════════════════════════════════════════════════════════

  baslat() {
    if (this.aktif) return;
    this.aktif = true;
    this.sonAciDegisimZamani = performance.now();
    this.modBaslangicZamani = performance.now();
    this.workpieceBoundsHesapla();
    this.takimPozGuncelle();
    this.kameraBaslangicAyarla();
    this.animasyonDongusuBaslat();
    this.durumYayinla();
    this.cd.detectChanges();
  }

  durdur() {
    this.aktif = false;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.durumYayinla();
    this.cd.detectChanges();
  }

  toggle() {
    this.aktif ? this.durdur() : this.baslat();
  }

  /** Pencereyi kapat */
  kapa() {
    this.durdur();
    this.muzikDurdur();
    this.kayitDurdur();
    this._pencere?.kapa?.();
  }

  // ════════════════════════════════════════════════════════════════════════
  // WORKPIECE & TAKİM HESAPLAMALARI
  // ════════════════════════════════════════════════════════════════════════

  private workpieceBoundsHesapla() {
    const noktalar = this.simComponent?.noktalar || this.noktalar;
    if (!noktalar?.length) return;

    const min = new THREE.Vector3(Infinity, Infinity, Infinity);
    const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

    for (const n of noktalar) {
      min.x = Math.min(min.x, n.x);
      min.y = Math.min(min.y, n.y);
      min.z = Math.min(min.z, n.z);
      max.x = Math.max(max.x, n.x);
      max.y = Math.max(max.y, n.y);
      max.z = Math.max(max.z, n.z);
    }

    this.workpieceMerkez.addVectors(min, max).multiplyScalar(0.5);
    this.workpieceBoyut.subVectors(max, min);

    // Z sınırları
    this.workpieceMinZ = min.z;
    this.workpieceMaxZ = max.z;

    // Minimum kamera yüksekliği: workpiece üstü + biraz margin
    const maxXY = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y);
    this.minKameraZ = this.workpieceMaxZ + maxXY * 0.15;
  }

  /** Kamerayı takım yönü boyunca konumlandır
   *  Z ekseni yerine takım yönü (takimYonu) kullanır.
   *  Spindle yönü yukarı (workpiece'ten uzağa) olduğundan kamera da o yönden bakar.
   */
  private kameraYukseklikAyarla(hedefPoz: THREE.Vector3): THREE.Vector3 {
    // Takım yönü spindle'ın baktığı yön (genellikle yukarı = workpiece'ten uzağa)
    // Kamera bu yönden bakmalı (takımın arkasından)
    const ustYon = this.takimYonu.clone().normalize();

    // Mevcut Z offset'i takım yönüne dönüştür
    // hedefPoz.z - takimPoz.z = istenen yükseklik farkı
    const yukseklikOffset = hedefPoz.z - this.takimPoz.z;

    // Z'yi takım pozisyonuna eşitle, sonra takım yönünde offset ekle
    hedefPoz.z = this.takimPoz.z;
    hedefPoz.addScaledVector(ustYon, yukseklikOffset);

    return hedefPoz;
  }

  private takimPozGuncelle() {
    const aktifNokta = this.simComponent?.aktifNokta || this.aktifNokta;
    if (!aktifNokta) return;

    this.takimPozOnceki.copy(this.takimPoz);
    this.takimPoz.set(aktifNokta.x, aktifNokta.y, aktifNokta.z);

    // Hız hesapla (frame-independent)
    this.takimHiz.subVectors(this.takimPoz, this.takimPozOnceki);

    // Hareket yönü hesapla (XY düzleminde - Z'yi görmezden gel sinematik için)
    const hizXY = new THREE.Vector2(this.takimHiz.x, this.takimHiz.y);
    if (hizXY.lengthSq() > 0.0001) {
      // Anlamlı hareket var
      this.hareketYonu.set(this.takimHiz.x, this.takimHiz.y, 0).normalize();
      // Yumuşatılmış hareket yönü (sinematik geçişler için)
      this.hareketYonuSmooth.lerp(this.hareketYonu, 0.05);
      this.hareketYonuSmooth.normalize();
    }

    // Takım ekseni yönü (spindle direction)
    if (aktifNokta.takimYonu) {
      this.takimYonu.copy(aktifNokta.takimYonu).normalize();
    }
  }

  private kameraBaslangicAyarla() {
    const kamera = this.sahne?.anaGosterim?.aktifKamera;
    const kontrol = this.sahne?.anaGosterim?.aktifKontrol;
    if (!kamera || !kontrol) return;

    this.kameraMevcutPoz.copy(kamera.position);
    this.kameraMevcutTarget.copy(kontrol.target);

    // Drone başlangıç değerleri
    const maxBoyut = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y, this.workpieceBoyut.z);
    this.droneMesafe = maxBoyut * 1.5;
    this.droneYukseklik = maxBoyut * 0.8;
    this.droneAci = Math.atan2(kamera.position.y - this.takimPoz.y, kamera.position.x - this.takimPoz.x);
  }

  // ════════════════════════════════════════════════════════════════════════
  // ANİMASYON DÖNGÜSÜ
  // ════════════════════════════════════════════════════════════════════════

  private animasyonDongusuBaslat() {
    const dongu = () => {
      if (!this.aktif) return;

      const simdi = performance.now();

      // Simülasyondan canlı veri al
      this.takimPozGuncelle();

      // Beat detection (müzik analizi) veya demo beat
      this.beatDetectionGuncelle(simdi);

      // Demo beat modu (müzik yokken simüle beat)
      if (this.demoBeat && !this.muzikOynatiliyor) {
        this.demoBeatGuncelle(simdi);
      }

      // Otomatik açı değişimi (serbest modda kapalı)
      if (this.otomatikAciDegisimi && !this.serbestKamera) {
        this.otomatikAciKontrol(simdi);
      }

      // Kamera güncelle (serbest modda atla - kullanıcı kontrolünde)
      if (!this.serbestKamera) {
        if (this.kameraGecisProgress < 1) {
          this.kameraGecisUygula();
        } else {
          this.kameraSinematikGuncelle(simdi);
        }
      } else {
        // Serbest modda sadece efektleri uygula (FOV/zoom)
        this.serbestModEfektUygula(simdi);
      }

      // Ritim senkronlu kamera efektleri
      const kamera = this.sahne?.anaGosterim?.aktifKamera;
      if (kamera) {
        this.ritimEfektleriniUygula(kamera);
      }

      // Kayıt süresi
      if (this.kayitYapiliyor) {
        this.kayitSuresi = (simdi - this.kayitBaslamaZamani) / 1000;
      }

      // Müzik pozisyonu
      if (this.muzikOynatiliyor && this.audioCtx) {
        this.muzikPozisyon = this.audioCtx.currentTime - this.muzikBaslangicZamani;
      }

      this.animFrameId = requestAnimationFrame(dongu);
    };

    this.animFrameId = requestAnimationFrame(dongu);
  }

  // ════════════════════════════════════════════════════════════════════════
  // OTOMATİK AÇI DEĞİŞİMİ
  // ════════════════════════════════════════════════════════════════════════

  private otomatikAciKontrol(simdi: number) {
    const aktifNokta = this.simComponent?.aktifNokta || this.aktifNokta;
    if (!aktifNokta) return;

    const oynatiliyor = this.simComponent?.simOynatiliyor ?? this.oynatiliyor;
    if (!oynatiliyor) return;

    const hareketTuru = aktifNokta.hareketTuru || '';
    const isleme = hareketTuru === 'İşleme' || hareketTuru === 'Isleme' || hareketTuru === 'ISLEME';

    // Hareket türü değiştiğinde açı değiştir
    if (hareketTuru !== this.oncekiHareketTuru) {
      this.oncekiHareketTuru = hareketTuru;

      if (isleme) {
        // Kesim başladı - dramatik yakın çekim veya drone
        const acilar: KameraAcisi[] = ['yakin', 'drone', 'takim'];
        this.aciDegistir(acilar[Math.floor(Math.random() * acilar.length)]);
        this.kesimAnindaZoom = true;
      } else {
        // Rapid hareket - geniş çekim
        const acilar: KameraAcisi[] = ['epik', 'ust', 'profil'];
        this.aciDegistir(acilar[Math.floor(Math.random() * acilar.length)]);
        this.kesimAnindaZoom = false;
      }
      this.sonAciDegisimZamani = simdi;
      return;
    }

    // Periyodik açı değişimi (sadece kesim sırasında)
    const minAralik = 4000 + (100 - this.aciDegisimSikligi) * 80;
    if (simdi - this.sonAciDegisimZamani > minAralik && isleme) {
      const acilar: KameraAcisi[] = ['drone', 'yakin', 'profil', 'takim', 'ust'];
      let yeniAci = acilar[Math.floor(Math.random() * acilar.length)];
      // Aynı açıyı seçmemeye çalış
      if (yeniAci === this.suankiAci && acilar.length > 1) {
        yeniAci = acilar[(acilar.indexOf(yeniAci) + 1) % acilar.length];
      }
      this.aciDegistir(yeniAci);
      this.sonAciDegisimZamani = simdi;
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // KAMERA AÇISI KONTROLÜ
  // ════════════════════════════════════════════════════════════════════════

  aciDegistir(yeniAci: KameraAcisi) {
    if (yeniAci === this.suankiAci && this.kameraGecisProgress >= 1) return;

    this.suankiAci = yeniAci;
    this.kameraGecisProgress = 0;
    this.modBaslangicZamani = performance.now();
    this.kameraHedefHesapla();
    this.kameraDegisti.emit(yeniAci);
    this.cd.detectChanges();
  }

  private kameraHedefHesapla() {
    const kamera = this.sahne?.anaGosterim?.aktifKamera;
    const kontrol = this.sahne?.anaGosterim?.aktifKontrol;
    if (!kamera || !kontrol) return;

    this.kameraMevcutPoz.copy(kamera.position);
    this.kameraMevcutTarget.copy(kontrol.target);

    const aktifNokta = this.simComponent?.aktifNokta || this.aktifNokta;
    const takimCap = aktifNokta?.takim?.cap || 20;
    const maxBoyut = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y, this.workpieceBoyut.z);

    // Hareket yönüne dik vektör (yandan bakış için)
    const dikYon = new THREE.Vector3(-this.hareketYonuSmooth.y, this.hareketYonuSmooth.x, 0).normalize();

    switch (this.suankiAci) {
      case 'drone':
        // Drone: Hareket yönünün önünden orbital
        this.droneMesafe = maxBoyut * 1.2;
        this.droneYukseklik = maxBoyut * 0.6;
        const hareketAcisi = Math.atan2(this.hareketYonuSmooth.y, this.hareketYonuSmooth.x);
        this.droneAci = hareketAcisi + Math.PI * 0.6; // Hareketin 60° önünde başla
        this.kameraHedefPoz.set(
          this.takimPoz.x + Math.cos(this.droneAci) * this.droneMesafe,
          this.takimPoz.y + Math.sin(this.droneAci) * this.droneMesafe,
          this.workpieceMaxZ + this.droneYukseklik
        );
        this.kameraYukseklikAyarla(this.kameraHedefPoz);
        this.kameraHedefTarget.copy(this.takimPoz);
        break;

      case 'yakin':
        // Close-up: Hareket yönüne dik açıdan yakın çekim
        const yakinMesafe = takimCap * 3.5;
        this.kameraHedefPoz.copy(this.takimPoz)
          .addScaledVector(dikYon, yakinMesafe)
          .setZ(this.workpieceMaxZ + takimCap * 2);
        this.kameraYukseklikAyarla(this.kameraHedefPoz);
        this.kameraHedefTarget.copy(this.takimPoz)
          .addScaledVector(this.hareketYonuSmooth, takimCap * 0.5);
        break;

      case 'profil':
        // Profil: Hareket yönüne dik yandan bakış
        const profilMesafe = maxBoyut * 0.7;
        this.kameraHedefPoz.copy(this.takimPoz)
          .addScaledVector(dikYon, profilMesafe)
          .setZ(this.workpieceMaxZ + maxBoyut * 0.25);
        this.kameraYukseklikAyarla(this.kameraHedefPoz);
        this.kameraHedefTarget.copy(this.takimPoz)
          .addScaledVector(this.hareketYonuSmooth, maxBoyut * 0.1);
        break;

      case 'ust':
        // Top-down: Yukarıdan, hafif arkadan
        const ustYukseklik = maxBoyut * 1.5;
        this.kameraHedefPoz.copy(this.takimPoz)
          .addScaledVector(this.hareketYonuSmooth, -maxBoyut * 0.15)
          .setZ(this.workpieceMaxZ + ustYukseklik);
        this.kameraYukseklikAyarla(this.kameraHedefPoz);
        this.kameraHedefTarget.copy(this.takimPoz)
          .addScaledVector(this.hareketYonuSmooth, maxBoyut * 0.2);
        break;

      case 'takim':
        // Tool POV: Hareket yönünün arkasından
        const arkaMesafe = takimCap * 4;
        const ileriBakis = takimCap * 15;
        this.kameraHedefPoz.copy(this.takimPoz)
          .addScaledVector(this.hareketYonuSmooth, -arkaMesafe)
          .setZ(this.workpieceMaxZ + takimCap * 2.5);
        this.kameraYukseklikAyarla(this.kameraHedefPoz);
        this.kameraHedefTarget.copy(this.takimPoz)
          .addScaledVector(this.hareketYonuSmooth, ileriBakis);
        break;

      case 'epik':
        // Epic: Hareket yönünün karşısından geniş çekim
        const epikMesafe = maxBoyut * 1.8;
        const karsiYon = new THREE.Vector3()
          .copy(this.hareketYonuSmooth)
          .negate()
          .applyAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI * 0.2);
        this.kameraHedefPoz.copy(this.workpieceMerkez)
          .addScaledVector(karsiYon, epikMesafe)
          .setZ(this.workpieceMaxZ + epikMesafe * 0.7);
        this.kameraYukseklikAyarla(this.kameraHedefPoz);
        this.kameraHedefTarget.lerpVectors(this.workpieceMerkez, this.takimPoz, 0.6);
        break;

      case 'beat':
        // Beat: Müzik ritmine göre hareket
        // Sabit kamera modunda mevcut pozisyonu koru
        if (this.beatSabitKamera) {
          this.kameraHedefPoz.copy(kamera.position);
          this.kameraHedefTarget.copy(kontrol.target);
        } else {
          // Hareketli mod: workpiece merkezine odaklan
          this.beatModMesafe = maxBoyut * 1.5;
          this.beatModYukseklik = maxBoyut * 0.8;
          this.beatModAci = 0;
          this.kameraHedefPoz.set(
            this.workpieceMerkez.x + this.beatModMesafe,
            this.workpieceMerkez.y,
            this.workpieceMaxZ + this.beatModYukseklik
          );
          this.kameraYukseklikAyarla(this.kameraHedefPoz);
          this.kameraHedefTarget.copy(this.workpieceMerkez);
        }
        break;

    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // KAMERA GEÇİŞ ANİMASYONU
  // ════════════════════════════════════════════════════════════════════════

  private kameraGecisUygula() {
    const kamera = this.sahne?.anaGosterim?.aktifKamera;
    const kontrol = this.sahne?.anaGosterim?.aktifKontrol;
    if (!kamera || !kontrol) return;

    // Hız: yavaş başla, hızlan, yavaş bitir (ease-in-out)
    const gecisHizi = 0.015 + (this.kameraHizi / 100) * 0.025;
    this.kameraGecisProgress = Math.min(1, this.kameraGecisProgress + gecisHizi);

    // Cubic ease-in-out
    const t = this.kameraGecisProgress < 0.5
      ? 4 * this.kameraGecisProgress * this.kameraGecisProgress * this.kameraGecisProgress
      : 1 - Math.pow(-2 * this.kameraGecisProgress + 2, 3) / 2;

    kamera.position.lerpVectors(this.kameraMevcutPoz, this.kameraHedefPoz, t);
    kontrol.target.lerpVectors(this.kameraMevcutTarget, this.kameraHedefTarget, t);

    kamera.lookAt(kontrol.target);
    kamera.updateProjectionMatrix();
    kontrol.update?.();
    this.sahne.anaGosterim.ciz?.();
  }

  // ════════════════════════════════════════════════════════════════════════
  // SİNEMATİK KAMERA HAREKETLERİ
  // ════════════════════════════════════════════════════════════════════════

  private kameraSinematikGuncelle(simdi: number) {
    const kamera = this.sahne?.anaGosterim?.aktifKamera;
    const kontrol = this.sahne?.anaGosterim?.aktifKontrol;
    if (!kamera || !kontrol) return;

    const aktifNokta = this.simComponent?.aktifNokta || this.aktifNokta;
    const takimCap = aktifNokta?.takim?.cap || 20;
    const lerpHiz = 0.03 + (this.kameraHizi / 100) * 0.07;
    const modSure = (simdi - this.modBaslangicZamani) / 1000;

    switch (this.suankiAci) {
      case 'drone':
        this.droneSinematik(kamera, kontrol, lerpHiz, modSure);
        break;
      case 'yakin':
        this.yakinCekimSinematik(kamera, kontrol, lerpHiz, takimCap);
        break;
      case 'profil':
        this.profilSinematik(kamera, kontrol, lerpHiz);
        break;
      case 'ust':
        this.ustSinematik(kamera, kontrol, lerpHiz);
        break;
      case 'takim':
        this.takimPOVSinematik(kamera, kontrol, lerpHiz, takimCap);
        break;
      case 'epik':
        this.epikSinematik(kamera, kontrol, lerpHiz, modSure);
        break;
      case 'beat':
        this.beatSinematik(kamera, kontrol, lerpHiz, modSure);
        break;
    }

    // Dramatik zoom (kesim anında) - beat modunda kullanılmaz
    if (this.dramatikZoom && this.suankiAci !== 'beat') {
      this.dramatikZoomUygula(kamera);
    }

    kamera.updateProjectionMatrix();
    kontrol.update?.();
    this.sahne.anaGosterim.ciz?.();
  }

  // ─── DRONE: Hareket yönünün önünden orbital takip ───
  private droneSinematik(kamera: any, kontrol: any, lerpHiz: number, modSure: number) {
    // Hareket yönüne göre "öne" doğru bias
    const hareketAcisi = Math.atan2(this.hareketYonuSmooth.y, this.hareketYonuSmooth.x);

    // Yavaş orbit - hareket yönünün önünde kal
    const orbitHiz = 0.0006 + (this.kameraHizi / 100) * 0.0015;
    const hedefDroneAci = hareketAcisi + Math.PI * 0.6; // Hareketin 60° önünde

    // Açıyı yumuşak geçiş ile hedefe yaklaştır
    const aciDelta = this.normalizeAngle(hedefDroneAci - this.droneAci);
    this.droneAci += aciDelta * 0.02 + orbitHiz;

    // Yükseklik ve mesafe hafif salınım
    const salinimHiz = 0.25;
    const mesafeSalinim = Math.sin(modSure * salinimHiz) * this.droneMesafe * 0.08;
    const yukseklikSalinim = Math.cos(modSure * salinimHiz * 0.6) * this.droneYukseklik * 0.12;

    const hedefMesafe = this.droneMesafe + mesafeSalinim;
    const hedefYukseklik = this.droneYukseklik + yukseklikSalinim;

    // Hedef pozisyon - takımın etrafında orbit
    const hedefPoz = new THREE.Vector3(
      this.takimPoz.x + Math.cos(this.droneAci) * hedefMesafe,
      this.takimPoz.y + Math.sin(this.droneAci) * hedefMesafe,
      this.workpieceMaxZ + hedefYukseklik // Workpiece üstünden yükseklik
    );
    this.kameraYukseklikAyarla(hedefPoz);

    // Yumuşak takip
    kamera.position.lerp(hedefPoz, lerpHiz);
    kontrol.target.lerp(this.takimPoz, lerpHiz * 1.2);
    kamera.lookAt(kontrol.target);
  }

  // ─── YAKIN: Hareket yönüne dik açıdan yakın çekim ───
  private yakinCekimSinematik(kamera: any, kontrol: any, lerpHiz: number, takimCap: number) {
    // Hareket yönüne 90° dik açıdan bak (kesimi en iyi gören açı)
    const dikYon = new THREE.Vector3(-this.hareketYonuSmooth.y, this.hareketYonuSmooth.x, 0).normalize();

    const yakinMesafe = takimCap * 3.5;

    // Hareket yönüne dik pozisyon + yükseklik (workpiece üstünden)
    const hedefPoz = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(dikYon, yakinMesafe)
      .setZ(this.workpieceMaxZ + takimCap * 2);
    this.kameraYukseklikAyarla(hedefPoz);

    // Biraz ilerisine bak (kesim noktası)
    const hedefTarget = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(this.hareketYonuSmooth, takimCap * 0.5);

    // Çok yumuşak takip
    kamera.position.lerp(hedefPoz, lerpHiz * 0.6);
    kontrol.target.lerp(hedefTarget, lerpHiz * 0.8);
    kamera.lookAt(kontrol.target);
  }

  // ─── PROFİL: Hareket yönüne paralel yandan takip ───
  private profilSinematik(kamera: any, kontrol: any, lerpHiz: number) {
    const maxBoyut = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y);
    const profilMesafe = maxBoyut * 0.7;

    // Hareket yönüne 90° dik yön (yandan bakış)
    const yanYon = new THREE.Vector3(-this.hareketYonuSmooth.y, this.hareketYonuSmooth.x, 0).normalize();

    // Takımın yanında, hareketle birlikte kayar (workpiece üstünden)
    const hedefPoz = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(yanYon, profilMesafe)
      .setZ(this.workpieceMaxZ + maxBoyut * 0.25);
    this.kameraYukseklikAyarla(hedefPoz);

    // Takımın biraz ilerisine bak
    const hedefTarget = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(this.hareketYonuSmooth, maxBoyut * 0.1);

    kamera.position.lerp(hedefPoz, lerpHiz);
    kontrol.target.lerp(hedefTarget, lerpHiz);
    kamera.lookAt(kontrol.target);
  }

  // ─── ÜST: Kuş bakışı - hareket yönü yukarı ───
  private ustSinematik(kamera: any, kontrol: any, lerpHiz: number) {
    const maxBoyut = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y, this.workpieceBoyut.z);
    const ustYukseklik = maxBoyut * 1.5;

    // Takımın üzerinde, hafif arkasında kal
    const hedefPoz = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(this.hareketYonuSmooth, -maxBoyut * 0.15) // Hafif arkadan
      .setZ(this.workpieceMaxZ + ustYukseklik);
    this.kameraYukseklikAyarla(hedefPoz);

    // Takımın biraz ilerisine bak
    const hedefTarget = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(this.hareketYonuSmooth, maxBoyut * 0.2);

    kamera.position.lerp(hedefPoz, lerpHiz);
    kontrol.target.lerp(hedefTarget, lerpHiz);

    // Kamera "up" vektörünü hareket yönüne ayarla (yol ekranda yukarı gider)
    const upVec = new THREE.Vector3(this.hareketYonuSmooth.x, this.hareketYonuSmooth.y, 0);
    kamera.up.lerp(upVec, lerpHiz * 0.5);
    kamera.up.normalize();
    kamera.lookAt(kontrol.target);
  }

  // ─── TAKİM POV: Hareket yönünden arkadan takip ───
  private takimPOVSinematik(kamera: any, kontrol: any, lerpHiz: number, takimCap: number) {
    // Hareket yönünün arkasından bak (araba takip kamerası gibi)
    const arkaMesafe = takimCap * 4;
    const ileriBakis = takimCap * 15;

    // Hareket yönünün arkasında kal (workpiece üstünden)
    const hedefPoz = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(this.hareketYonuSmooth, -arkaMesafe)
      .setZ(this.workpieceMaxZ + takimCap * 2.5);
    this.kameraYukseklikAyarla(hedefPoz);

    // İleri bak - hareket yönünde
    const hedefTarget = new THREE.Vector3()
      .copy(this.takimPoz)
      .addScaledVector(this.hareketYonuSmooth, ileriBakis);

    kamera.position.lerp(hedefPoz, lerpHiz * 1.2); // Hızlı takip
    kontrol.target.lerp(hedefTarget, lerpHiz * 1.2);
    kamera.lookAt(kontrol.target);
  }

  // ─── EPİK: Hareket yönünün karşısından geniş çekim ───
  private epikSinematik(kamera: any, kontrol: any, lerpHiz: number, modSure: number) {
    const maxBoyut = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y, this.workpieceBoyut.z);
    const epikMesafe = maxBoyut * 1.8;

    // Hareket yönünün karşısından + hafif yandan bak
    const karsiYon = new THREE.Vector3()
      .copy(this.hareketYonuSmooth)
      .negate()
      .applyAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI * 0.2); // 36° açı ekle

    // Yavaş dolly - hareket yönüne göre pozisyon
    const dollyOffset = Math.sin(modSure * 0.08) * maxBoyut * 0.15;

    const hedefPoz = new THREE.Vector3()
      .copy(this.workpieceMerkez)
      .addScaledVector(karsiYon, epikMesafe + dollyOffset)
      .setZ(this.workpieceMaxZ + epikMesafe * 0.7);
    this.kameraYukseklikAyarla(hedefPoz);

    // Workpiece merkezi ile takım arasında - takıma daha çok ağırlık
    const hedefTarget = new THREE.Vector3().lerpVectors(this.workpieceMerkez, this.takimPoz, 0.6);

    kamera.position.lerp(hedefPoz, lerpHiz * 0.4); // Yavaş hareket
    kontrol.target.lerp(hedefTarget, lerpHiz * 0.5);
    kamera.lookAt(kontrol.target);
  }

  // ─── BEAT: Müzik ritmine göre sinematik hareket ───
  private beatSinematik(kamera: any, kontrol: any, lerpHiz: number, modSure: number) {
    const maxBoyut = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y, this.workpieceBoyut.z);

    // === BEAT TEPKİ ÇARPANLARI (Gelişmiş Ayarlardan) ===
    const zoomTepki = this.beatZoomTepki / 50;           // Zoom tepki şiddeti (0-2)
    const orbitTepki = this.beatOrbitTepki / 50;         // Orbit hız artış tepkisi (0-2)
    const mesafeTepki = this.beatMesafeTepki / 50;       // Kamera yaklaşma tepkisi (0-2)
    const yukseklikTepki = this.beatYukseklikTepki / 50; // Kamera yükselme tepkisi (0-2)
    const shakeTepki = this.beatShakeTepki / 50;         // Shake tepkisi (0-2)
    const targetTepki = this.beatTargetTepki / 50;       // Target salınım tepkisi (0-2)
    const tepkiHizi = this.beatTepkiHizi / 50;           // Genel tepki hızı (0-2)
    const enerjiCarpani = 0.5 + (this.beatEnerjiCarpani / 100); // Enerji çarpanı (0.5-1.5)

    // Beat panel slider'ları
    const zoomCarpan = this.beatZoomEfekti / 50;   // 0-2 arası
    const orbitHizCarpan = this.beatOrbitHizi / 50; // 0-2 arası
    const shakeCarpan = this.beatShakeEfekti / 50; // 0-2 arası

    // Hareket tipine göre enerji yumuşatma
    let smoothFactor = 0.85;
    let responseFactor = 0.15;
    switch (this.beatHareketTipi) {
      case 'aggressive':
        smoothFactor = 0.7;
        responseFactor = 0.3;
        break;
      case 'pulse':
        smoothFactor = 0.5;
        responseFactor = 0.5;
        break;
    }

    // Enerji çarpanı ile beat yoğunluğu hesapla
    const rawIntensity = this.beatEnerjiSmooth * enerjiCarpani;
    this.beatModIntensity = this.beatModIntensity * smoothFactor + rawIntensity * responseFactor;

    // === SABİT KAMERA MODU: Sadece zoom efekti (opsiyonel takım izleme) ===
    if (this.beatSabitKamera) {
      const baseFov = 55;

      // Takım izleme aktifse target'ı güncelle
      if (this.beatSabitTakimIzle) {
        const hedefNokta = this.beatTakimTakip ? this.takimPoz : this.workpieceMerkez;
        const targetLerpHiz = 0.05 * tepkiHizi;
        kontrol.target.lerp(hedefNokta, targetLerpHiz);
        kamera.lookAt(kontrol.target);
      }

      // FOV/Zoom efekti uygula (beat tepkisine göre)
      if (kamera.isPerspectiveCamera) {
        let zoomAmount = this.beatModIntensity * 25 * zoomCarpan * zoomTepki;
        if (this.beatHareketTipi === 'pulse') {
          zoomAmount = this.beatModIntensity > 0.5 ? 30 * zoomCarpan * zoomTepki : 0;
        }
        const beatFov = THREE.MathUtils.clamp(baseFov - zoomAmount, 30, 80);
        const fovLerpHiz = (this.beatHareketTipi === 'pulse' ? 0.2 : 0.1) * tepkiHizi;
        kamera.fov = THREE.MathUtils.lerp(kamera.fov, beatFov, fovLerpHiz);
        kamera.updateProjectionMatrix();
      } else if (kamera.isOrthographicCamera) {
        const baseZoom = 1;
        let zoomAmount = this.beatModIntensity * 0.6 * zoomCarpan * zoomTepki;
        if (this.beatHareketTipi === 'pulse') {
          zoomAmount = this.beatModIntensity > 0.5 ? 0.8 * zoomCarpan * zoomTepki : 0;
        }
        kamera.zoom = THREE.MathUtils.lerp(kamera.zoom, baseZoom + zoomAmount, 0.1 * tepkiHizi);
        kamera.updateProjectionMatrix();
      }

      kontrol.update?.();
      this.sahne.anaGosterim.ciz?.();
      return; // Kamera pozisyonu değişmez
    }

    // === HEDEF NOKTA: Takım veya Workpiece Merkezi ===
    const hedefNokta = this.beatTakimTakip ? this.takimPoz : this.workpieceMerkez;
    const targetLerpHiz = (this.beatHareketTipi === 'aggressive' ? 0.1 : 0.05) * tepkiHizi;
    this.beatModMevcutTarget.lerp(hedefNokta, targetLerpHiz);

    // === ORBİT HAREKETI (Orbit tepkisine göre) ===
    let baseOrbitSpeed = 0.08 * orbitHizCarpan * this.beatOrbitYonu;
    let energyBoost = this.beatModIntensity * 0.5 * orbitHizCarpan * orbitTepki * this.beatOrbitYonu;

    if (this.beatHareketTipi === 'aggressive') {
      energyBoost *= 1.5;
    } else if (this.beatHareketTipi === 'pulse') {
      baseOrbitSpeed = 0;
      energyBoost *= 2.5;
    }

    const orbitSpeed = baseOrbitSpeed + energyBoost;

    // Açıyı güncelle
    if (this.beatAciAtlama) {
      const aciDelta = this.normalizeAngle(this.beatModHedefAci - this.beatModAci);
      const atlamaHizi = (this.beatHareketTipi === 'aggressive' ? 0.1 : 0.05) * tepkiHizi;
      this.beatModAci += aciDelta * atlamaHizi + orbitSpeed * 0.016;
    } else {
      this.beatModAci += orbitSpeed * 0.016;
    }

    // === MESAFE VE YÜKSEKLİK (Beat tepkisine göre) ===
    // Mesafe: beat'te yaklaş (mesafeTepki'ye göre)
    let mesafeAzalma = this.beatModIntensity * 0.4 * zoomCarpan * mesafeTepki;
    // Yükseklik: beat'te yüksel (yukseklikTepki'ye göre)
    let yukseklikArtis = this.beatModIntensity * 0.5 * zoomCarpan * yukseklikTepki;

    if (this.beatHareketTipi === 'aggressive') {
      mesafeAzalma *= 1.3;
      yukseklikArtis *= 0.8;
    } else if (this.beatHareketTipi === 'pulse') {
      mesafeAzalma = this.beatModIntensity > 0.5 ? 0.5 * zoomCarpan * mesafeTepki : 0;
      yukseklikArtis = this.beatModIntensity > 0.5 ? 0.7 * zoomCarpan * yukseklikTepki : 0;
    }

    // Mesafe ve yükseklik hesapla
    const dinamikMesafe = this.beatModMesafe * (1 - mesafeAzalma * 0.5);
    const dinamikYukseklik = this.beatModYukseklik * (1 + yukseklikArtis * 0.5);

    // Ritme senkron salınım
    const salınımHiz = this.beatHareketTipi === 'aggressive' ? 3 : 2;
    const ritimSalinim = Math.sin(modSure * salınımHiz) * maxBoyut * 0.08 * this.beatModIntensity;

    // === KAMERA POZİSYONU ===
    const hedefPoz = new THREE.Vector3(
      this.beatModMevcutTarget.x + Math.cos(this.beatModAci) * dinamikMesafe,
      this.beatModMevcutTarget.y + Math.sin(this.beatModAci) * dinamikMesafe,
      this.workpieceMaxZ + dinamikYukseklik + ritimSalinim
    );
    this.kameraYukseklikAyarla(hedefPoz);

    // === SHAKE EFEKTİ (Beat tepkisine göre) ===
    if (this.beatShakeOffset.lengthSq() > 0.001) {
      const shakeMultiplier = this.beatHareketTipi === 'aggressive' ? 1.5 : 1;
      hedefPoz.add(this.beatShakeOffset.clone().multiplyScalar(shakeCarpan * shakeMultiplier * shakeTepki));
    }

    // === TARGET SALINIM (Beat tepkisine göre) ===
    const baseTargetSiddet = this.beatHareketTipi === 'pulse' ? 0.05 : 0.03;
    const targetSalınımSiddet = baseTargetSiddet * targetTepki;
    const targetSalinim = new THREE.Vector3(
      Math.sin(modSure * 1.5) * maxBoyut * targetSalınımSiddet * this.beatModIntensity,
      Math.cos(modSure * 1.2) * maxBoyut * targetSalınımSiddet * this.beatModIntensity,
      Math.sin(modSure * 0.8) * maxBoyut * targetSalınımSiddet * 0.7 * this.beatModIntensity
    );
    const hedefTarget = this.beatModMevcutTarget.clone().add(targetSalinim);

    // === HAREKET UYGULA (Tepki hızına göre) ===
    let dinamikLerpHiz: number;
    switch (this.beatHareketTipi) {
      case 'aggressive':
        dinamikLerpHiz = lerpHiz * (0.6 + this.beatModIntensity * 2.5) * tepkiHizi;
        break;
      case 'pulse':
        dinamikLerpHiz = lerpHiz * (0.2 + this.beatModIntensity * 4) * tepkiHizi;
        break;
      default: // smooth
        dinamikLerpHiz = lerpHiz * (0.4 + this.beatModIntensity * 2) * tepkiHizi;
    }

    kamera.position.lerp(hedefPoz, dinamikLerpHiz);
    kontrol.target.lerp(hedefTarget, dinamikLerpHiz * 0.9);
    kamera.lookAt(kontrol.target);

    // === FOV / ZOOM EFEKTİ (Beat tepkisine göre) ===
    const baseFov = 55;
    if (kamera.isPerspectiveCamera) {
      let zoomAmount = this.beatModIntensity * 20 * zoomCarpan * zoomTepki;
      if (this.beatHareketTipi === 'pulse') {
        zoomAmount = this.beatModIntensity > 0.5 ? 25 * zoomCarpan * zoomTepki : 0;
      }
      const beatFov = THREE.MathUtils.clamp(baseFov - zoomAmount, 30, 80);
      const fovLerpHiz = (this.beatHareketTipi === 'pulse' ? 0.15 : 0.08) * tepkiHizi;
      kamera.fov = THREE.MathUtils.lerp(kamera.fov, beatFov, fovLerpHiz);
    } else if (kamera.isOrthographicCamera) {
      const baseZoom = 1;
      let zoomAmount = this.beatModIntensity * 0.5 * zoomCarpan * zoomTepki;
      if (this.beatHareketTipi === 'pulse') {
        zoomAmount = this.beatModIntensity > 0.5 ? 0.7 * zoomCarpan * zoomTepki : 0;
      }
      kamera.zoom = THREE.MathUtils.lerp(kamera.zoom, baseZoom + zoomAmount, 0.08 * tepkiHizi);
    }
  }

  // ─── Açı normalizasyonu (-PI, PI) ───
  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  }

  // ─── DEMO BEAT: Müzik yokken simüle beat ───
  private demoBeatGuncelle(simdi: number) {
    // Demo beat interval'ına göre beat tetikle
    if (simdi - this.demoBeatZamani >= this.demoBeatInterval) {
      this.demoBeatZamani = simdi;

      // Yapay beat enerjisi oluştur (sinüs dalga)
      const phase = (simdi % (this.demoBeatInterval * 4)) / (this.demoBeatInterval * 4);
      const demoEnerji = 0.3 + Math.sin(phase * Math.PI * 2) * 0.4 + Math.random() * 0.3;

      this.beatEnerji = demoEnerji;
      this.beatEnerjiSmooth = this.beatEnerjiSmooth * 0.6 + demoEnerji * 0.4;

      // Beat tetikle
      this.beatTetikle();
    } else {
      // Beat arası decay
      this.beatEnerjiSmooth *= 0.95;
    }
  }

  // ─── SERBEST MOD: Kullanıcı kontrolünde, sadece efektler ───
  private serbestModEfektUygula(simdi: number) {
    const kamera = this.sahne?.anaGosterim?.aktifKamera;
    const kontrol = this.sahne?.anaGosterim?.aktifKontrol;
    if (!kamera || !kontrol) return;

    const modSure = (simdi - this.modBaslangicZamani) / 1000;

    // Beat tepki çarpanları
    const zoomTepki = this.beatZoomTepki / 50;
    const tepkiHizi = this.beatTepkiHizi / 50;
    const enerjiCarpani = 0.5 + (this.beatEnerjiCarpani / 100);
    const zoomCarpan = this.beatZoomEfekti / 50;

    // Beat yoğunluğu hesapla
    const rawIntensity = this.beatEnerjiSmooth * enerjiCarpani;
    this.beatModIntensity = this.beatModIntensity * 0.85 + rawIntensity * 0.15;

    // Sadece FOV/Zoom efekti uygula (kamera pozisyonu değişmez)
    const baseFov = 55;
    if (kamera.isPerspectiveCamera) {
      let zoomAmount = this.beatModIntensity * 25 * zoomCarpan * zoomTepki;
      if (this.beatHareketTipi === 'pulse') {
        zoomAmount = this.beatModIntensity > 0.5 ? 30 * zoomCarpan * zoomTepki : 0;
      }
      const beatFov = THREE.MathUtils.clamp(baseFov - zoomAmount, 30, 80);
      const fovLerpHiz = (this.beatHareketTipi === 'pulse' ? 0.2 : 0.1) * tepkiHizi;
      kamera.fov = THREE.MathUtils.lerp(kamera.fov, beatFov, fovLerpHiz);
      kamera.updateProjectionMatrix();
    } else if (kamera.isOrthographicCamera) {
      const baseZoom = 1;
      let zoomAmount = this.beatModIntensity * 0.6 * zoomCarpan * zoomTepki;
      if (this.beatHareketTipi === 'pulse') {
        zoomAmount = this.beatModIntensity > 0.5 ? 0.8 * zoomCarpan * zoomTepki : 0;
      }
      kamera.zoom = THREE.MathUtils.lerp(kamera.zoom, baseZoom + zoomAmount, 0.1 * tepkiHizi);
      kamera.updateProjectionMatrix();
    }

    // Takım izleme aktifse target'ı güncelle (opsiyonel)
    if (this.beatSabitTakimIzle && this.suankiAci === 'beat') {
      const hedefNokta = this.beatTakimTakip ? this.takimPoz : this.workpieceMerkez;
      const targetLerpHiz = 0.03 * tepkiHizi;
      kontrol.target.lerp(hedefNokta, targetLerpHiz);
    }

    kontrol.update?.();
    this.sahne.anaGosterim.ciz?.();
  }

  // ─── DRAMATİK ZOOM ───
  private dramatikZoomUygula(kamera: any) {
    const aktifNokta = this.simComponent?.aktifNokta || this.aktifNokta;
    if (!aktifNokta) return;

    const hareketTuru = aktifNokta.hareketTuru || '';
    const isleme = hareketTuru === 'İşleme' || hareketTuru === 'Isleme' || hareketTuru === 'ISLEME';

    // Hedef FOV (perspective) veya Zoom (orthographic)
    if (kamera.isPerspectiveCamera) {
      const bazFov = 50;
      const hedefFov = isleme
        ? bazFov - (this.zoomSiddeti / 100) * 15 // Kesimde zoom in (FOV azalt)
        : bazFov + (this.zoomSiddeti / 100) * 10; // Rapidde zoom out (FOV artır)

      const fovHiz = 0.02;
      kamera.fov = THREE.MathUtils.lerp(kamera.fov, hedefFov, fovHiz);
    } else if (kamera.isOrthographicCamera) {
      const bazZoom = 1;
      const hedefZoom = isleme
        ? bazZoom * (1 + (this.zoomSiddeti / 100) * 0.3)
        : bazZoom * (1 - (this.zoomSiddeti / 100) * 0.15);

      const zoomHiz = 0.02;
      kamera.zoom = THREE.MathUtils.lerp(kamera.zoom, hedefZoom, zoomHiz);
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // VİDEO KAYIT SİSTEMİ
  // ════════════════════════════════════════════════════════════════════════

  kayitBaslat() {
    if (this.kayitYapiliyor) return;
    if (!this.sahne?.anaGosterim?.canvas?.nativeElement) {
      console.warn('Director Mode: Canvas bulunamadı');
      return;
    }

    const canvas = this.sahne.anaGosterim.canvas.nativeElement as HTMLCanvasElement;
    const stream = canvas.captureStream(30);

    if (this.muzikYuklendi && this.audioCtx) {
      const audioDestination = this.audioCtx.createMediaStreamDestination();
      if (this.audioGain) {
        this.audioGain.connect(audioDestination);
      }
      stream.addTrack(audioDestination.stream.getAudioTracks()[0]);
    }

    try {
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: this.kayitKalitesi
      });
    } catch (e) {
      this.mediaRecorder = new MediaRecorder(stream);
    }

    this.kayitChunks = [];

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.kayitChunks.push(e.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.kayitChunks, { type: 'video/webm' });
      this.kayitBitti.emit(blob);
      this.videoIndir(blob);
    };

    this.mediaRecorder.start(100);
    this.kayitYapiliyor = true;
    this.kayitBaslamaZamani = performance.now();
    this.kayitSuresi = 0;
    this.cd.detectChanges();
  }

  kayitDurdur() {
    if (!this.kayitYapiliyor || !this.mediaRecorder) return;

    this.mediaRecorder.stop();
    this.kayitYapiliyor = false;
    this.cd.detectChanges();
  }

  private videoIndir(blob: Blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const tarih = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    a.download = `FikoYT_CNC_${tarih}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ════════════════════════════════════════════════════════════════════════
  // MÜZİK SİSTEMİ
  // ════════════════════════════════════════════════════════════════════════

  muzikSec() {
    this.muzikInputRef?.nativeElement?.click();
  }

  async muzikDosyaYuklendi(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.muzikIsim = file.name;

    if (!this.audioCtx) {
      this.audioCtx = new AudioContext();
      this.audioGain = this.audioCtx.createGain();
      this.audioGain.connect(this.audioCtx.destination);

      // Beat detection için AnalyserNode kur
      this.audioAnalyser = this.audioCtx.createAnalyser();
      this.audioAnalyser.fftSize = 256; // Hızlı analiz için küçük FFT
      this.audioAnalyser.smoothingTimeConstant = 0.3;
      this.analyserData = new Uint8Array(this.audioAnalyser.frequencyBinCount);
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      this.audioBuffer = await this.audioCtx.decodeAudioData(arrayBuffer);
      this.muzikSure = this.audioBuffer.duration;
      this.muzikYuklendi = true;
      this.enerjiGecmisi = []; // Enerji geçmişini sıfırla
      this.cd.detectChanges();
    } catch (e) {
      console.error('Müzik dosyası okunamadı:', e);
    }
  }

  muzikOynat() {
    if (!this.muzikYuklendi || !this.audioBuffer || !this.audioCtx) return;

    if (this.audioSource) {
      this.audioSource.stop();
    }

    this.audioSource = this.audioCtx.createBufferSource();
    this.audioSource.buffer = this.audioBuffer;

    // Beat detection için analyser'a bağla
    if (this.audioAnalyser) {
      this.audioSource.connect(this.audioAnalyser);
      this.audioAnalyser.connect(this.audioGain);
    } else {
      this.audioSource.connect(this.audioGain);
    }

    this.audioSource.start(0, this.muzikPozisyon);

    this.muzikBaslangicZamani = this.audioCtx.currentTime - this.muzikPozisyon;
    this.muzikOynatiliyor = true;

    this.audioSource.onended = () => {
      this.muzikOynatiliyor = false;
      this.muzikPozisyon = 0;
      this.cd.detectChanges();
    };

    this.cd.detectChanges();
  }

  muzikDuraklat() {
    if (!this.muzikOynatiliyor || !this.audioSource) return;

    this.audioSource.stop();
    this.muzikPozisyon = this.audioCtx.currentTime - this.muzikBaslangicZamani;
    this.muzikOynatiliyor = false;
    this.cd.detectChanges();
  }

  muzikDurdur() {
    if (this.audioSource) {
      try { this.audioSource.stop(); } catch {}
    }
    this.muzikOynatiliyor = false;
    this.muzikPozisyon = 0;
    this.cd.detectChanges();
  }

  muzikSesAyarla(ses: number) {
    this.muzikSes = ses;
    if (this.audioGain) {
      this.audioGain.gain.value = ses / 100;
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // BEAT DETECTION & RİTİM SENKRON
  // ════════════════════════════════════════════════════════════════════════

  /** Her frame'de müzik analizi yap ve beat algıla */
  private beatDetectionGuncelle(simdi: number) {
    // Beat modunda veya ritimSenkron açıkken çalış
    const beatModuAktif = this.suankiAci === 'beat';
    if ((!this.ritimSenkron && !beatModuAktif) || !this.muzikOynatiliyor || !this.audioAnalyser || !this.analyserData) {
      // Efektleri yavaşça sıfırla
      this.beatZoomPulse *= 0.9;
      this.beatShakeOffset.multiplyScalar(0.85);
      this.beatModIntensity *= 0.95;
      return;
    }

    // Frekans verisini al
    this.audioAnalyser.getByteFrequencyData(this.analyserData);

    // Bass frekansları (0-8 bin) - beat için en önemli
    const bassEnd = Math.floor(this.analyserData.length * 0.15);
    let bassEnerji = 0;
    for (let i = 0; i < bassEnd; i++) {
      bassEnerji += this.analyserData[i];
    }
    bassEnerji = bassEnerji / (bassEnd * 255); // Normalize (0-1)

    // Mid frekanslar (genel ritim için)
    const midStart = bassEnd;
    const midEnd = Math.floor(this.analyserData.length * 0.5);
    let midEnerji = 0;
    for (let i = midStart; i < midEnd; i++) {
      midEnerji += this.analyserData[i];
    }
    midEnerji = midEnerji / ((midEnd - midStart) * 255);

    // Toplam enerji (ayarlanabilir ağırlıklar)
    const bassWeight = this.beatBassAgirlik / 100;
    const midWeight = this.beatMidAgirlik / 100;
    const toplamAgirlik = bassWeight + midWeight || 1;
    this.beatEnerji = (bassEnerji * bassWeight + midEnerji * midWeight) / toplamAgirlik;

    // Yumuşatılmış enerji
    this.beatEnerjiSmooth = this.beatEnerjiSmooth * 0.8 + this.beatEnerji * 0.2;

    // Enerji geçmişi (adaptif eşik için)
    this.enerjiGecmisi.push(this.beatEnerji);
    if (this.enerjiGecmisi.length > 60) {
      this.enerjiGecmisi.shift();
    }

    // Ortalama enerji hesapla
    const ortalamaEnerji = this.enerjiGecmisi.reduce((a, b) => a + b, 0) / this.enerjiGecmisi.length;

    // Beat algılama: ani enerji artışı + cooldown
    // Hassasiyet: 0 = çok düşük hassasiyet (yüksek eşik), 100 = çok hassas (düşük eşik)
    const beatEsik = 1 - (this.beatHassasiyet / 100) * 0.7; // 0.3 - 1.0 arası
    const enerjiArtisi = this.beatEnerji - this.oncekiEnerji;
    const adaptifEsik = Math.max(beatEsik, ortalamaEnerji * 1.5);
    const beatAlgilandi = enerjiArtisi > 0.15 &&
                          this.beatEnerji > adaptifEsik &&
                          (simdi - this.sonBeatZamani) > this.beatCooldownAyar;

    if (beatAlgilandi) {
      this.sonBeatZamani = simdi;
      this.beatTetikle();
    }

    this.oncekiEnerji = this.beatEnerji;

    // Efektleri zamanla azalt
    this.beatZoomPulse *= 0.92;
    this.beatShakeOffset.multiplyScalar(0.88);
  }

  /** Beat algılandığında efektleri tetikle */
  private beatTetikle() {
    const siddet = this.ritimSiddeti / 100;

    // UI slider değerlerini çarpanlara dönüştür (0-2 arası)
    const orbitCarpan = this.beatOrbitHizi / 50;
    const zoomCarpan = this.beatZoomEfekti / 50;
    const shakeCarpan = this.beatShakeEfekti / 50;

    // Beat modunda özel davranış
    if (this.suankiAci === 'beat') {
      const maxBoyut = Math.max(this.workpieceBoyut.x, this.workpieceBoyut.y, this.workpieceBoyut.z);

      // Açı atlama aktifse ve güçlü beat'te yeni açıya atla
      if (this.beatAciAtlama && this.beatEnerji > 0.55) {
        // Orbit hızına göre kadran sayısı (hızlı = daha fazla kadran)
        const kadranSayisi = Math.floor(4 + orbitCarpan * 2 + Math.random() * 2);
        let yeniKadran = Math.floor(Math.random() * kadranSayisi);
        if (yeniKadran === this.beatModSonPozisyon) {
          yeniKadran = (yeniKadran + 1 + Math.floor(Math.random() * 2)) % kadranSayisi;
        }
        this.beatModSonPozisyon = yeniKadran;
        // Orbit hızına göre açı atlama miktarı
        const aciOffset = (Math.random() - 0.5) * 0.5 * orbitCarpan;
        this.beatModHedefAci = (yeniKadran * Math.PI * 2 / kadranSayisi) + aciOffset;
      }

      // Mesafe pulse - zoom efektine göre yaklaş/uzaklaş
      // zoomCarpan yüksekse = daha yakın, düşükse = daha uzak
      const mesafeFaktor = 1.5 - (zoomCarpan * 0.4);
      this.beatModMesafe = maxBoyut * (0.8 + Math.random() * 0.4) * mesafeFaktor;
      this.beatModYukseklik = maxBoyut * (0.3 + Math.random() * 0.5) * (1 + zoomCarpan * 0.3);

      // Shake efekti - shakeCarpan'a göre şiddet
      const shakeAmount = 8.0 * siddet * this.beatEnerji * shakeCarpan;
      this.beatShakeOffset.set(
        (Math.random() - 0.5) * shakeAmount,
        (Math.random() - 0.5) * shakeAmount,
        (Math.random() - 0.5) * shakeAmount * 0.5
      );
      return;
    }

    // Normal modlar için standart efektler
    // Zoom pulse
    this.beatZoomPulse = 0.15 * siddet * (0.7 + Math.random() * 0.3);

    // Kamera shake (rastgele yön)
    const shakeAmount = 2.0 * siddet;
    this.beatShakeOffset.set(
      (Math.random() - 0.5) * shakeAmount,
      (Math.random() - 0.5) * shakeAmount,
      (Math.random() - 0.5) * shakeAmount * 0.5
    );

    // Güçlü beat'lerde otomatik açı değişimi (düşük olasılıkla)
    if (this.otomatikAciDegisimi && this.beatEnerji > 0.85 && Math.random() < 0.15) {
      const acilar: KameraAcisi[] = ['drone', 'yakin', 'profil', 'takim'];
      const yeniAci = acilar[Math.floor(Math.random() * acilar.length)];
      if (yeniAci !== this.suankiAci) {
        this.aciDegistir(yeniAci);
      }
    }
  }

  /** Ritim senkronlu kamera efektlerini uygula */
  private ritimEfektleriniUygula(kamera: any) {
    // Beat modunda bu fonksiyon kullanılmaz - kendi efektleri var
    if (!this.ritimSenkron || !this.muzikOynatiliyor || this.suankiAci === 'beat') return;

    const siddet = this.ritimSiddeti / 100;

    // Shake offset uygula
    if (this.beatShakeOffset.lengthSq() > 0.001) {
      kamera.position.add(this.beatShakeOffset);
    }

    // Zoom pulse (FOV veya camera zoom)
    if (this.beatZoomPulse > 0.01) {
      if (kamera.isPerspectiveCamera) {
        // FOV pulse - beat'te daral (zoom in efekti)
        kamera.fov -= this.beatZoomPulse * 8 * siddet;
      } else if (kamera.isOrthographicCamera) {
        // Ortho zoom pulse
        kamera.zoom += this.beatZoomPulse * 0.3 * siddet;
      }
    }

    // Enerji bazlı kamera hızı modülasyonu (daha enerjik = daha hızlı)
    // Bu değeri kamera hareketlerinde kullanabiliriz
  }

  /** Anlık beat enerjisini al (0-1) - diğer efektler için */
  get anlikBeatEnerji(): number {
    return this.beatEnerjiSmooth;
  }

  // ════════════════════════════════════════════════════════════════════════
  // YARDIMCI
  // ════════════════════════════════════════════════════════════════════════

  private durumYayinla() {
    this.durumDegisti.emit({
      aktif: this.aktif,
      kayitYapiliyor: this.kayitYapiliyor,
      muzikYuklendi: this.muzikYuklendi,
      suankiAci: this.suankiAci
    });
  }

  sureFormatla(saniye: number): string {
    const dk = Math.floor(saniye / 60);
    const sn = Math.floor(saniye % 60);
    return `${dk.toString().padStart(2, '0')}:${sn.toString().padStart(2, '0')}`;
  }

  get aciListesi() {
    return Object.entries(this.KAMERA_ACILARI).map(([key, val]) => ({
      key: key as KameraAcisi,
      ...val
    }));
  }
}
